import { Fragment, useState } from "react";
import { Chip } from "../../../components/ui/Chip";
import type { ReadsAsLookup } from "./designationParts";
import { GradeRowDetail } from "./GradeRowDetail";
import type { DesignationView, GradeLinkView } from "../types";

interface Props {
  designation: DesignationView;
  gradeLinks: GradeLinkView[];
  readsAsByCode: ReadsAsLookup;
  canWrite: boolean;
  onTdsChanged: () => void;
}

const COLUMN_TITLES = ["Grade", "Supplier", "Application", "Source", "TDS"];
const COLUMN_COUNT = COLUMN_TITLES.length + 1;
const HEADER_CELL_CLASSES =
  "bg-rs-black px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-rs-light-gray border-b border-rs-jet whitespace-nowrap";
const GRADE_CELL_CLASSES = "px-3 py-2.5 align-top border-t border-rs-jet";
const MUTED = <span className="text-[#a9b0a1]">—</span>;

export function GradeTable({ designation, gradeLinks, readsAsByCode, canWrite, onTdsChanged }: Props) {
  const [expandedLinkIds, setExpandedLinkIds] = useState<Set<number>>(new Set());

  const toggleGradeRow = (gradeDesignationLinkId: number) =>
    setExpandedLinkIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(gradeDesignationLinkId)) nextIds.delete(gradeDesignationLinkId);
      else nextIds.add(gradeDesignationLinkId);
      return nextIds;
    });

  return (
    <div className="border-t border-[#3c4235] bg-rs-black">
      <table className="w-full border-collapse text-[13px]" aria-label={`Grades for ${designation.designationCode}`}>
        <thead>
          <tr>
            <th className={`${HEADER_CELL_CLASSES} w-9`}>
              <span className="sr-only">Details</span>
            </th>
            {COLUMN_TITLES.map((title) => (
              <th key={title} className={HEADER_CELL_CLASSES}>
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {gradeLinks.length === 0 && (
            <tr>
              <td colSpan={COLUMN_COUNT} className="px-3 py-2.5 text-[#a9b0a1]">
                No grades are attached to this RS code yet.
              </td>
            </tr>
          )}
          {gradeLinks.map((gradeLink) => {
            const isExpanded = expandedLinkIds.has(gradeLink.gradeDesignationLinkId);
            return (
              <Fragment key={gradeLink.gradeDesignationLinkId}>
                <tr>
                  <td className={GRADE_CELL_CLASSES}>
                    <button
                      type="button"
                      className={`grid size-5.5 place-items-center rounded border bg-transparent p-0 text-[10px] cursor-pointer transition-transform hover:border-rs-green hover:text-rs-green ${
                        isExpanded ? "rotate-90 border-rs-green/35 text-rs-green" : "border-rs-jet text-[#a9b0a1]"
                      }`}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? "Hide" : "Show"} details for ${gradeLink.tradename}`}
                      onClick={() => toggleGradeRow(gradeLink.gradeDesignationLinkId)}
                    >
                      ▶
                    </button>
                  </td>
                  <td className={`${GRADE_CELL_CLASSES} text-white`}>{gradeLink.tradename}</td>
                  <td className={`${GRADE_CELL_CLASSES} text-rs-off-white`}>{gradeLink.supplier}</td>
                  <td className={`${GRADE_CELL_CLASSES} text-rs-off-white`}>{gradeLink.processingApplication || MUTED}</td>
                  <td className={`${GRADE_CELL_CLASSES} text-rs-off-white`}>{gradeLink.dataSource || MUTED}</td>
                  <td className={`${GRADE_CELL_CLASSES} whitespace-nowrap`}>
                    {gradeLink.tdsFileCount ? (
                      <Chip variant="ok">{gradeLink.tdsFileCount} TDS</Chip>
                    ) : (
                      <span className="text-[#a9b0a1]">None</span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td />
                    <td colSpan={COLUMN_TITLES.length} className="px-3 pb-2">
                      <GradeRowDetail
                        designation={designation}
                        gradeLink={gradeLink}
                        readsAsByCode={readsAsByCode}
                        canWrite={canWrite}
                        onTdsChanged={onTdsChanged}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
